import camelcaseKeys from "camelcase-keys";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import decamelize from "decamelize";

// Setup dayjs timezone plugin and set default to Asia/Seoul
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Seoul");

export interface ApiClientResponse {
    response: Response;
    jsonResponse: any;
    camelizedResponse: any;
}

export const apiClient = async (endpoint: string, options: RequestInit = {}): Promise<ApiClientResponse> => {
    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL as string;
    const token = localStorage.getItem("access_token");
    const defaultHeaders: HeadersInit = {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const config: RequestInit = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };
    const requestUrl = `${baseUrl}${endpoint}`;

    try {
        const response = await fetch(requestUrl, config);
        const jsonResponse = await response.json();

        if (!response.ok) {
            const errorMsg = jsonResponse.error || jsonResponse.message || "Server returned an error response.";
            console.error("Server Error:", errorMsg);

            throw new Error(errorMsg, { cause: { response, jsonResponse } });
        }

        const camelizedResponse = camelcaseKeys(jsonResponse, { deep: true });

        return {
            response,
            jsonResponse,
            camelizedResponse,
        };
    } catch (error) {
        if (error instanceof SyntaxError) {
            console.error("Failed to parse JSON response:", error);
            throw new Error("Failed to parse the server response as JSON.", { cause: error });
        } else if (error instanceof Error && error.cause) {
            console.error("Server Error:", error);
            throw error;
        } else {
            console.error("Network or server error:", error);
            throw new Error("A network error or server issue occurred.", { cause: error });
        }
    }
};

export function convertToFormData(entity: Object): FormData {
    const formData = new FormData();

    Object.keys(entity).forEach((key) => {
        appendToFormData(formData, decamelize(key), (entity as any)[key]);
    });

    return formData;
}

export function appendToFormData(formData: FormData, key: string, value: any) {
    if (dayjs.isDayjs(value)) {
        // Send as Seoul local time (YYYY-MM-DDTHH:mm:ss) without timezone conversion
        formData.append(key, value.format('YYYY-MM-DDTHH:mm:ss'));
    } else if (value instanceof File) {
        formData.append(key, value);
    } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
            appendToFormData(formData, `${key}[${index}]`, item);
        });
    } else if (value !== null && typeof value === "object") {
        Object.keys(value).forEach((subKey) => {
            appendToFormData(formData, `${key}[${decamelize(subKey)}]`, value[subKey]);
        });
    } else if (value != null) {
        // Only append non-null and non-undefined values
        formData.append(key, value.toString());
    }
    // Skip appending null or undefined values
}

// Shared by services that PATCH a single field/reward as
// urlencoded form data (e.g. roulette/attendance reward updates).
export async function patchFormUrlEncoded(endpoint: string, entity: Object): Promise<any> {
    try {
        const urlEncodedBody = new URLSearchParams(convertToFormData(entity) as any).toString();

        const { camelizedResponse } = await apiClient(endpoint, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: urlEncodedBody,
        });
        return camelizedResponse;
    } catch (error) {
        console.error("Error updating challenge: ", error);
        throw error;
    }
}

export function toQueryParams(params: Record<string, any>): string {
    return new URLSearchParams(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    ).toString();
}

export function transformDates<T>(data: any): T {
    const transformedData: any = { ...data };

    Object.keys(transformedData).forEach((key) => {
        if (isDateField(key) && transformedData[key] !== null) {
            // Parse as Seoul timezone (backend sends Seoul local time)
            transformedData[key] = dayjs.tz(transformedData[key], "Asia/Seoul");
        }
    });

    return transformedData as T;
}

export function isDateField(key: string): boolean {
    const dateFields = ["publishAt", "startAt", "endAt", "winnersAnnouncedAt", "createdAt", "updatedAt", "lastAccessAt"];

    return dateFields.includes(key);
}